/**
 * In-app audio / video calls in the Inbox (TRUS-668).
 *
 * Typed client for /api/messaging/calls. The backend owns the contract:
 * Twilio Programmable Video rooms, one active call per channel, members may
 * only join / leave / decline calls in their own member DM.
 *
 * Two transports share one client:
 *  - `callsApi`       staff — the axios `apiClient` (auth + X-CBO-ID interceptors)
 *  - `memberCallsApi` member portal — `memberApiFetch` (member token + X-CBO-ID).
 *    Member screens never use the staff apiClient (lib/memberAuth.ts).
 *
 * `getConfig()` never throws: a 403 calls_disabled, a 404 (backend not deployed
 * yet) or a network failure all mean "no call affordances" → enabled: false.
 *
 * v2 (calls_contract_v2.md): screen sharing is client-only (a video track named
 * "screen"); call notes & transcripts add `notes_enabled`, `record` on start,
 * `consent_recording` on join (409 consent_required), the recording / notes
 * fields on Call, and GET /{id}/notes. Every v2 field is optional: older
 * servers omit them, and absent means "off".
 *
 * v3 (calls_contract_v3.md §3): staff invite staff / members who are not in
 * the conversation — POST / GET /{id}/invites, and `invited` /
 * `can_open_channel` on Call. A server without v3 omits both fields (and 404s
 * the invites endpoints): `invitesSupported()` is the gate.
 *
 * v4 (calls_contract_v4.md): a screen share's track name carries what is
 * shared (`screen:monitor` | `screen:window` | `screen:browser`; any name
 * starting with "screen" is a share). In-call recording adds
 * POST /{id}/recording/start | stop | consent, and `recording_active`,
 * `recording_started_by`, `can_record` on Call plus `recording` per
 * participant. A server without v4 omits them: `recordingControlsSupported()`
 * is the gate.
 *
 * v5 (calls_contract_v5.md §3): GET /notes lists a conversation's (or every
 * readable conversation's) recorded calls with their notes status, and
 * searches summaries / action items / decisions / transcripts (`q`). Staff
 * only: the member client refuses `listNotes()` without a request. A server
 * without v5 404s it; the panel shows an error state, nothing else changes.
 */

import { apiClient } from './apiClient';
import { getAuthToken } from '@/lib/authFetch';
import { memberApiFetch } from '@/lib/memberAuth';

export const CALLS_BASE = '/api/messaging/calls';

export type CallMode = 'audio' | 'video';
export type CallStatus = 'active' | 'ended';
export type CallNotesStatus = 'none' | 'pending' | 'processing' | 'ready' | 'failed';
/**
 * Why a call has no notes (yet), from the backend (staff only; 2026-10-22 "Calls
 * are not creating notes"). Absent / null when notes exist or are on the way.
 */
export type CallNotesReason =
  | 'in_progress'
  /** TRUS-708: notes_status stays pending while the worker waits for its keys. */
  | 'waiting_for_configuration'
  | 'failed'
  | 'not_processed'
  | 'notes_off'
  | 'not_configured'
  | 'members_not_allowed'
  | 'not_recorded';

const NOTES_REASONS = new Set<CallNotesReason>([
  'in_progress',
  'waiting_for_configuration',
  'failed',
  'not_processed',
  'notes_off',
  'not_configured',
  'members_not_allowed',
  'not_recorded',
]);

/** A known notes reason, else null (an older or newer server). */
export function notesReasonOf(value: unknown): CallNotesReason | null {
  return typeof value === 'string' && NOTES_REASONS.has(value as CallNotesReason) ? (value as CallNotesReason) : null;
}

/** The Twilio track name a screen share is published under (contract v2 §1). */
export const SCREEN_TRACK_NAME = 'screen';

/** What a screen share shows (getDisplayMedia's displaySurface, contract v4 §1). */
export type ScreenSurface = 'monitor' | 'window' | 'browser';
const SCREEN_SURFACES = new Set<string>(['monitor', 'window', 'browser']);

/** A surface the browser reported, or null when it did not say / said something else. */
export function normalizeScreenSurface(value: unknown): ScreenSurface | null {
  return typeof value === 'string' && SCREEN_SURFACES.has(value) ? (value as ScreenSurface) : null;
}

/** `screen:<surface>` (v4), or plain `screen` when the surface is unknown. */
export function screenTrackName(surface: ScreenSurface | null | undefined): string {
  return surface ? `${SCREEN_TRACK_NAME}:${surface}` : SCREEN_TRACK_NAME;
}

/** Any track whose name starts with "screen" is a screen share (older clients send plain "screen"). */
export function isScreenTrackName(name: string | null | undefined): boolean {
  return typeof name === 'string' && name.startsWith(SCREEN_TRACK_NAME);
}

/** The surface a screen track's name carries, or null (plain "screen", or not a share). */
export function screenSurfaceOfTrackName(name: string | null | undefined): ScreenSurface | null {
  if (!isScreenTrackName(name)) return null;
  return normalizeScreenSurface((name as string).slice(SCREEN_TRACK_NAME.length + 1));
}

/** v4: a participant's answer to an in-call recording. */
export type CallRecordingConsent = 'consented' | 'declined' | 'pending';

export interface CallParticipant {
  identity: string;
  /** 'guest' (TRUS-692): a meeting guest from a guest link, identity guest-<id>. */
  kind: 'staff' | 'member' | 'guest';
  user_id: number | null;
  member_id: number | null;
  name: string;
  joined_at: string | null;
  left_at: string | null;
  /** v4: their answer to the recording (absent on older servers). */
  recording?: CallRecordingConsent;
}

export interface Call {
  id: number;
  channel_id: number;
  mode: CallMode;
  status: CallStatus;
  /** Why an ended call ended ('hangup' | 'ended' | 'declined' | 'expired' …), when the server says. */
  ended_reason?: string | null;
  room_name: string;
  room_type: 'go' | 'group';
  /** DM vs channel (room_type no longer tells). Older servers omit it. */
  is_dm?: boolean;
  started_by: { user_id: number | null; name: string };
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  message_id: number | null;
  participants: CallParticipant[];
  active_participant_count: number;
  is_participant: boolean;
  can_end: boolean;
  /** v2: the call is being recorded for notes + a transcript. */
  recording?: boolean;
  /** v2: the caller has consented to the recording. */
  my_recording_consent?: boolean;
  /** v2: where the notes pipeline is. */
  notes_status?: CallNotesStatus;
  /** v2: the "Call notes" channel message, once posted. */
  notes_message_id?: number | null;
  /** Why there are no notes (yet) - staff only. */
  notes_reason?: CallNotesReason | null;
  /** The reason a failed call's notes failed, or what a waiting call needs - staff only. */
  notes_error?: string | null;
  /** TRUS-708: POST /{id}/notes/retry would be accepted - staff only. */
  notes_retryable?: boolean;
  /** TRUS-708: the recording was deleted, so the notes can never be made. */
  notes_media_deleted?: boolean;
  /** v3: the caller was invited to this call (not necessarily a channel member). */
  invited?: boolean;
  /**
   * v3: the caller can read the channel. False for an invitee without access:
   * open the call panel alone, never the conversation.
   */
  can_open_channel?: boolean;
  /** v3 (if the server sends it): who invited the caller. */
  invited_by?: { user_id: number | null; name: string } | null;
  /** v4: audio is being recorded right now (`recording` stays true after a stop, for the notes). */
  recording_active?: boolean;
  /** v4: who started the current recording. */
  recording_started_by?: { user_id: number | null; name: string } | null;
  /** v4: the caller may start / stop recording mid-call. */
  can_record?: boolean;
  /** TRUS-691/692: the scheduled meeting this call belongs to (meetings in-call lobby). */
  meeting_id?: number | null;
}

export type CallInviteKind = 'staff' | 'member';
export type CallInviteRejectReason = 'not_in_org' | 'already_in_call' | 'recording_not_allowed' | 'limit' | string;

/** POST /{id}/invites */
export interface CallInviteResult {
  invited: { kind: CallInviteKind; user_id: number | null; member_id: number | null; name: string }[];
  rejected: { kind: CallInviteKind; id: number; reason: CallInviteRejectReason }[];
  links: { staff: string | null; member: string | null };
}

/** GET /{id}/invites */
export interface CallInvite {
  kind: CallInviteKind;
  user_id: number | null;
  member_id: number | null;
  name: string;
  invited_by: { user_id: number | null; name: string } | null;
  invited_at: string | null;
  joined: boolean;
}

/** Max invitees per call (contract v3). */
export const MAX_CALL_INVITES = 25;

/** The server speaks v3 (invites) for this call. */
export function invitesSupported(call: Pick<Call, 'can_open_channel'> | null | undefined): boolean {
  return typeof call?.can_open_channel === 'boolean';
}

/** The server speaks v4 (in-call recording) for this call: both fields are present. */
export function recordingControlsSupported(
  call: Pick<Call, 'can_record' | 'recording_active'> | null | undefined,
): boolean {
  return typeof call?.can_record === 'boolean' && typeof call?.recording_active === 'boolean';
}

export interface CallJoin {
  call: Call;
  token: string;
  identity: string;
  room_name: string;
  mode: CallMode;
  expires_in: number;
}

export interface CallsConfig {
  enabled: boolean;
  can_start: boolean;
  provider: string;
  /** v2: call notes & transcripts can be offered (capability on + providers configured). */
  notes_enabled: boolean;
  /**
   * TRUS-690: calls that include members (member DMs, member invitees) can be
   * recorded too (`messaging_call_notes_members`). Their notes stay staff-only.
   */
  notes_with_members: boolean;
}

export interface CallTranscriptLine {
  speaker: string;
  text: string;
  start_ms: number;
}

/** GET /{id}/notes */
export interface CallNotes {
  status: CallNotesStatus;
  summary: string | null;
  action_items: string[];
  decisions: string[];
  transcript: CallTranscriptLine[];
  created_at: string | null;
  error: string | null;
  /** TRUS-708 (older servers omit these). */
  notes_reason: CallNotesReason | null;
  notes_retryable: boolean;
  notes_media_deleted: boolean;
}

/** `metadata.call_notes` on the "Call notes" channel message. */
export interface CallNotesMessageMeta {
  call_id: number;
  notes_id: number | null;
}

/** v5: which part of the notes a search matched. */
export type CallNotesMatchField = 'summary' | 'action_items' | 'decisions' | 'transcript';

/** v5: one row of GET /notes. */
export interface CallNotesListItem {
  call_id: number;
  channel_id: number;
  channel_name: string | null;
  is_dm: boolean;
  mode: CallMode;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  started_by: { user_id: number | null; name: string } | null;
  notes_status: CallNotesStatus;
  /** Why there are no notes (yet): in progress, failed, never processed. */
  notes_reason: CallNotesReason | null;
  /** The reason the notes failed, when they did (or what a waiting call needs). */
  notes_error: string | null;
  /** TRUS-708: "Retry notes" would be accepted. */
  notes_retryable: boolean;
  /** TRUS-708: the recording was deleted. */
  notes_media_deleted: boolean;
  /** The full summary when ready. */
  summary: string | null;
  action_items_count: number;
  decisions_count: number;
  /** Only with `q`: where it matched, and ≤ 200 chars of plain text around it. */
  match: { field: CallNotesMatchField; snippet: string } | null;
}

/** v5: GET /notes. */
export interface CallNotesListPage {
  items: CallNotesListItem[];
  /** Cursor for the next page (pass as `before`), null on the last page. */
  next_before: string | null;
}

export interface ListCallNotesParams {
  /** Only this conversation. Omit for every conversation the caller can read. */
  channelId?: number | null;
  /** Search text; sent only when it is 2–200 chars after trimming. */
  q?: string | null;
  /** Page size (server default 20, max 50). */
  limit?: number;
  /** `next_before` of the previous page. */
  before?: string | null;
}

/** Search text the server accepts (contract v5: 2–200 chars after trim), else null. */
export function normalizeNotesQuery(q: string | null | undefined): string | null {
  const trimmed = (q ?? '').trim();
  if (trimmed.length < 2) return null;
  return trimmed.slice(0, 200);
}

export interface StartCallResponse {
  call: Call;
  join: CallJoin;
  already_active: boolean;
}

/** `metadata.call` on the "Started a … call" channel message. */
export interface CallMessageMeta {
  call_id: number;
  mode: CallMode;
  status: CallStatus;
  ended_at?: string | null;
  duration_seconds?: number | null;
  /** Why it ended, when the backend says (e.g. a 1:1 call nobody answered: declined / missed). */
  ended_reason?: string | null;
  /** v2: the notes pipeline status, mirrored onto the call message. */
  notes_status?: CallNotesStatus;
  /** v2: the call is / was recorded. */
  recording?: boolean;
  /** Why an ended, unrecorded call has no notes (a reason code, never text). */
  notes_reason?: CallNotesReason | null;
}

export const DISABLED_CALLS_CONFIG: CallsConfig = {
  enabled: false,
  can_start: false,
  provider: 'twilio',
  notes_enabled: false,
  notes_with_members: false,
};

/** An error from the calls API, with the backend's machine-readable `code`. */
export class CallsApiError extends Error {
  status: number;
  code: string | null;
  /** v2: a 409 consent_required names the recording call to consent to. */
  callId: number | null;
  constructor(message: string, status: number, code: string | null, callId: number | null = null) {
    super(message);
    this.name = 'CallsApiError';
    this.status = status;
    this.code = code;
    this.callId = callId;
  }
}

export interface CallsTransport {
  get<T>(path: string, params?: Record<string, string | number>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  /** Best-effort POST that survives the page closing (pagehide / unload). */
  postKeepalive(path: string): void;
}

export interface CallsApi {
  kind: 'staff' | 'member';
  getConfig(): Promise<CallsConfig>;
  /** `record` (v2) asks for notes + a transcript; the starter's consent is implied. */
  start(channelId: number, mode: CallMode, options?: { record?: boolean }): Promise<StartCallResponse>;
  active(channelId?: number): Promise<Call[]>;
  get(callId: number): Promise<Call>;
  /** v2: `consentRecording` answers a 409 consent_required. */
  join(callId: number, options?: { consentRecording?: boolean }): Promise<CallJoin>;
  leave(callId: number): Promise<Call>;
  /** Fire-and-forget leave for pagehide / window close. */
  leaveOnUnload(callId: number): void;
  decline(callId: number): Promise<{ ok: boolean }>;
  end(callId: number): Promise<Call>;
  /** v2: GET /{id}/notes (404 if the call never recorded). */
  getNotes(callId: number): Promise<CallNotes>;
  /** v5: list / search recorded calls and their notes (staff only). */
  listNotes(params?: ListCallNotesParams): Promise<CallNotesListPage>;
  /**
   * TRUS-708: make a failed / waiting call's notes again (staff only). 409
   * notes_already_processing / notes_done, 410 recording_deleted.
   */
  retryNotes(callId: number): Promise<Call>;
  /** v3: invite staff (user ids) and members (member ids) into the call. Staff only. */
  invite(callId: number, invitees: { userIds?: number[]; memberIds?: number[] }): Promise<CallInviteResult>;
  /** v3: who has been invited. */
  listInvites(callId: number): Promise<CallInvite[]>;
  /** v4: start recording mid-call (staff who can end the call). */
  startRecording(callId: number): Promise<Call>;
  /** v4: stop recording (the notes are still made when the call ends). */
  stopRecording(callId: number): Promise<Call>;
  /** v4: allow / don't allow my audio in the recording. */
  setRecordingConsent(callId: number, allow: boolean): Promise<Call>;
}

function errorFromBody(status: number, data: any, fallback: string): CallsApiError {
  const detail = data?.detail;
  const message =
    typeof detail === 'string' ? detail : typeof detail?.message === 'string' ? detail.message : fallback;
  const code = (typeof data?.code === 'string' && data.code) || (typeof detail?.code === 'string' && detail.code) || null;
  const rawCallId = Number(data?.call_id ?? detail?.call_id);
  const callId = Number.isFinite(rawCallId) && rawCallId > 0 ? rawCallId : null;
  return new CallsApiError(message, status, code, callId);
}

export function createCallsApi(transport: CallsTransport, kind: 'staff' | 'member'): CallsApi {
  return {
    kind,
    async getConfig() {
      try {
        const cfg = await transport.get<Partial<CallsConfig>>(`${CALLS_BASE}/config`);
        return {
          enabled: !!cfg?.enabled,
          can_start: !!cfg?.enabled && !!cfg?.can_start,
          provider: cfg?.provider || 'twilio',
          notes_enabled: !!cfg?.enabled && !!cfg?.notes_enabled,
          notes_with_members: !!cfg?.enabled && !!cfg?.notes_enabled && !!cfg?.notes_with_members,
        };
      } catch {
        return DISABLED_CALLS_CONFIG;
      }
    },
    start: (channelId, mode, options) =>
      transport.post<StartCallResponse>(
        CALLS_BASE,
        // `record` only when asked: older servers never see the new field.
        options?.record ? { channel_id: channelId, mode, record: true } : { channel_id: channelId, mode },
      ),
    async active(channelId) {
      const res = await transport.get<{ calls: Call[] }>(
        `${CALLS_BASE}/active`,
        channelId != null ? { channel_id: channelId } : undefined,
      );
      return res?.calls ?? [];
    },
    async get(callId) {
      const res = await transport.get<{ call: Call }>(`${CALLS_BASE}/${callId}`);
      return res.call;
    },
    join: (callId, options) =>
      transport.post<CallJoin>(
        `${CALLS_BASE}/${callId}/join`,
        options?.consentRecording ? { consent_recording: true } : undefined,
      ),
    async leave(callId) {
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/leave`);
      return res.call;
    },
    leaveOnUnload: (callId) => transport.postKeepalive(`${CALLS_BASE}/${callId}/leave`),
    decline: (callId) => transport.post<{ ok: boolean }>(`${CALLS_BASE}/${callId}/decline`),
    async end(callId) {
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/end`);
      return res.call;
    },
    async getNotes(callId) {
      const res = await transport.get<Partial<CallNotes>>(`${CALLS_BASE}/${callId}/notes`);
      return {
        status: (res?.status as CallNotesStatus) || 'none',
        summary: res?.summary ?? null,
        action_items: Array.isArray(res?.action_items) ? res!.action_items : [],
        decisions: Array.isArray(res?.decisions) ? res!.decisions : [],
        transcript: Array.isArray(res?.transcript) ? res!.transcript : [],
        created_at: res?.created_at ?? null,
        error: res?.error ?? null,
        notes_reason: notesReasonOf(res?.notes_reason),
        notes_retryable: res?.notes_retryable === true,
        notes_media_deleted: res?.notes_media_deleted === true,
      };
    },
    async retryNotes(callId) {
      // Notes are staff-only: the member client never asks (the server would 404).
      if (kind === 'member') {
        throw new CallsApiError('Members cannot retry call notes', 404, 'not_found');
      }
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/notes/retry`);
      return res.call;
    },
    async listNotes(params = {}) {
      // Members never see the list (the server would 403 members_cannot_list_notes).
      if (kind === 'member') {
        throw new CallsApiError('Members cannot list call notes', 403, 'members_cannot_list_notes');
      }
      const query: Record<string, string | number> = {};
      if (params.channelId != null) query.channel_id = params.channelId;
      const q = normalizeNotesQuery(params.q);
      if (q) query.q = q;
      if (params.limit != null && Number.isFinite(params.limit)) {
        query.limit = Math.min(50, Math.max(1, Math.floor(params.limit)));
      }
      if (params.before) query.before = params.before;
      const res = await transport.get<unknown>(
        `${CALLS_BASE}/notes`,
        Object.keys(query).length ? query : undefined,
      );
      return parseCallNotesListPage(res);
    },
    async invite(callId, invitees) {
      const res = await transport.post<Partial<CallInviteResult>>(`${CALLS_BASE}/${callId}/invites`, {
        user_ids: invitees.userIds ?? [],
        member_ids: invitees.memberIds ?? [],
      });
      return {
        invited: Array.isArray(res?.invited) ? res!.invited : [],
        rejected: Array.isArray(res?.rejected) ? res!.rejected : [],
        links: { staff: res?.links?.staff || null, member: res?.links?.member || null },
      };
    },
    async listInvites(callId) {
      const res = await transport.get<{ invites?: CallInvite[] }>(`${CALLS_BASE}/${callId}/invites`);
      return Array.isArray(res?.invites) ? res!.invites : [];
    },
    async startRecording(callId) {
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/recording/start`);
      return res.call;
    },
    async stopRecording(callId) {
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/recording/stop`);
      return res.call;
    },
    async setRecordingConsent(callId, allow) {
      const res = await transport.post<{ call: Call }>(`${CALLS_BASE}/${callId}/recording/consent`, { allow });
      return res.call;
    },
  };
}

// ---------------------------------------------------------------------------
// Staff transport (axios apiClient)
// ---------------------------------------------------------------------------

function axiosError(err: any): CallsApiError {
  const status = err?.response?.status ?? 0;
  return errorFromBody(status, err?.response?.data, err?.message || 'Request failed');
}

export const staffCallsTransport: CallsTransport = {
  async get(path, params) {
    try {
      const res = await apiClient.get(path, params ? { params } : undefined);
      return res.data;
    } catch (err) {
      throw axiosError(err);
    }
  },
  async post(path, body) {
    try {
      const res = await apiClient.post(path, body ?? {});
      return res.data;
    } catch (err) {
      throw axiosError(err);
    }
  },
  postKeepalive(path) {
    try {
      const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = getAuthToken() || localStorage.getItem('member_token');
      if (token) headers.Authorization = `Bearer ${token}`;
      const cbo = localStorage.getItem('active_cbo_id');
      if (cbo) headers['X-CBO-ID'] = cbo;
      void fetch(`${base}${path}`, { method: 'POST', headers, body: '{}', keepalive: true }).catch(() => {});
    } catch {
      /* best effort */
    }
  },
};

// ---------------------------------------------------------------------------
// Member transport (memberApiFetch — member token, never the staff client)
// ---------------------------------------------------------------------------

async function memberJson<T>(res: Response, fallback: string): Promise<T> {
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) throw errorFromBody(res.status, data, fallback);
  return data as T;
}

export const memberCallsTransport: CallsTransport = {
  async get(path, params) {
    const qs = params
      ? `?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`
      : '';
    const res = await memberApiFetch(`${path}${qs}`);
    return memberJson(res, 'Request failed');
  },
  async post(path, body) {
    const res = await memberApiFetch(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
    return memberJson(res, 'Request failed');
  },
  postKeepalive(path) {
    try {
      void memberApiFetch(path, { method: 'POST', body: '{}', keepalive: true }).catch(() => {});
    } catch {
      /* best effort */
    }
  },
};

export const callsApi = createCallsApi(staffCallsTransport, 'staff');
export const memberCallsApi = createCallsApi(memberCallsTransport, 'member');

/** "3m 12s", "45s", "1h 02m". */
export function formatCallDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  const s = Math.floor(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** Live timer text "mm:ss" / "h:mm:ss". */
export function formatCallClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Parse a backend timestamp that may omit its 'Z'. */
export function parseCallTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const t = new Date(hasTz ? iso : `${iso}Z`).getTime();
  return Number.isNaN(t) ? null : t;
}

/** The call card metadata on a message, if any. */
export function callMetaOf(metadata: Record<string, any> | null | undefined): CallMessageMeta | null {
  const c = metadata?.call;
  if (!c || typeof c !== 'object') return null;
  const id = Number(c.call_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    call_id: id,
    mode: c.mode === 'audio' ? 'audio' : 'video',
    status: c.status === 'ended' ? 'ended' : 'active',
    ended_at: c.ended_at ?? null,
    duration_seconds: typeof c.duration_seconds === 'number' ? c.duration_seconds : null,
    ended_reason: typeof c.ended_reason === 'string' ? c.ended_reason : null,
    notes_status: NOTES_STATUSES.has(c.notes_status) ? c.notes_status : undefined,
    recording: typeof c.recording === 'boolean' ? c.recording : undefined,
    notes_reason: notesReasonOf(c.notes_reason) ?? undefined,
  };
}

const NOTES_STATUSES = new Set<CallNotesStatus>(['none', 'pending', 'processing', 'ready', 'failed']);

/** Notes are on their way (the pipeline is still running). */
export function isNotesPending(status: CallNotesStatus | null | undefined): boolean {
  return status === 'pending' || status === 'processing';
}

/** The call-notes card metadata on a message, if any. */
export function callNotesMetaOf(metadata: Record<string, any> | null | undefined): CallNotesMessageMeta | null {
  const n = metadata?.call_notes;
  if (!n || typeof n !== 'object') return null;
  const id = Number(n.call_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const notesId = Number(n.notes_id);
  return { call_id: id, notes_id: Number.isFinite(notesId) && notesId > 0 ? notesId : null };
}

const MATCH_FIELDS = new Set<CallNotesMatchField>(['summary', 'action_items', 'decisions', 'transcript']);

function countOf(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** One GET /notes row, or null when it is unusable (no call / channel id). */
export function parseCallNotesListItem(raw: unknown): CallNotesListItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, any>;
  const callId = Number(r.call_id);
  const channelId = Number(r.channel_id);
  if (!Number.isFinite(callId) || callId <= 0 || !Number.isFinite(channelId) || channelId <= 0) return null;
  const sb = r.started_by;
  const startedBy =
    sb && typeof sb === 'object' && typeof sb.name === 'string'
      ? { user_id: Number.isFinite(Number(sb.user_id)) && sb.user_id != null ? Number(sb.user_id) : null, name: sb.name }
      : null;
  const m = r.match;
  const match =
    m && typeof m === 'object' && MATCH_FIELDS.has(m.field) && typeof m.snippet === 'string'
      ? { field: m.field as CallNotesMatchField, snippet: m.snippet }
      : null;
  return {
    call_id: callId,
    channel_id: channelId,
    channel_name: textOrNull(r.channel_name),
    is_dm: r.is_dm === true,
    mode: r.mode === 'audio' ? 'audio' : 'video',
    started_at: textOrNull(r.started_at),
    ended_at: textOrNull(r.ended_at),
    duration_seconds:
      typeof r.duration_seconds === 'number' && Number.isFinite(r.duration_seconds) && r.duration_seconds >= 0
        ? r.duration_seconds
        : null,
    started_by: startedBy,
    notes_status: NOTES_STATUSES.has(r.notes_status) ? r.notes_status : 'none',
    notes_reason: notesReasonOf(r.notes_reason),
    notes_error: textOrNull(r.notes_error),
    notes_retryable: r.notes_retryable === true,
    notes_media_deleted: r.notes_media_deleted === true,
    summary: textOrNull(r.summary),
    action_items_count: countOf(r.action_items_count),
    decisions_count: countOf(r.decisions_count),
    match,
  };
}

/** GET /notes, defensively: a missing / malformed body is an empty last page. */
export function parseCallNotesListPage(raw: unknown): CallNotesListPage {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  const items = Array.isArray(r.items)
    ? r.items.map(parseCallNotesListItem).filter((i: CallNotesListItem | null): i is CallNotesListItem => i != null)
    : [];
  return { items, next_before: textOrNull(r.next_before) };
}

/** "1:05" from milliseconds (transcript timestamps). */
export function formatTranscriptTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '';
  return formatCallClock(ms / 1000);
}
